import React from 'react';
import './Skeleton.css';

/**
 * Skeleton — typography-aware loading placeholder used in place of
 * a spinning circle while content is fetched.
 *
 * Three shapes the rest of the app composes from:
 *  - `<Skeleton.Block />`  any rectangular region; pass `aspectRatio`
 *                          for a square media tile, or `height` for
 *                          fixed-height bars (titles / paragraphs).
 *  - `<Skeleton.Line />`   single horizontal text bar (~1em tall).
 *  - `<Skeleton.Card />`   pre-composed product-card placeholder
 *                          (square media + 2 lines + price line).
 *                          Use this in product grids so the layout
 *                          doesn't reflow when real data arrives.
 *
 * Animation is a calm cream→soft-cream pulse via `--motion-fade`,
 * disabled under `prefers-reduced-motion: reduce`. No travelling
 * shimmer — the audit's motion contract excludes horizontal sweep
 * animations.
 */
const Block = ({
  className = '',
  width,
  height,
  aspectRatio,
  rounded = 'md',
  style: extraStyle,
  ...rest
}) => {
  const style = {
    width,
    height,
    aspectRatio,
    ...extraStyle,
  };
  return (
    <span
      className={`skeleton skeleton--${rounded} ${className}`}
      style={style}
      aria-hidden="true"
      {...rest}
    />
  );
};

const Line = ({ width = '100%', className = '', ...rest }) => (
  <Block height="1em" width={width} rounded="pill" className={`skeleton-line ${className}`} {...rest} />
);

const Card = ({ className = '' }) => (
  <div className={`skeleton-card ${className}`} aria-hidden="true">
    <Block aspectRatio="1 / 1" rounded="md" className="skeleton-card__media" />
    <Line width="80%" className="skeleton-card__title" />
    <Line width="60%" className="skeleton-card__sub" />
    <Line width="40%" className="skeleton-card__price" />
  </div>
);

const Skeleton = { Block, Line, Card };

export default Skeleton;
